/* app/seo-dashboard.jsx - SEO Dashboard landing + shared SEO shell helpers.

   THE FOUR QUESTIONS ARE THE SAME FOUR AT BOTH POSITIONS, which is the one
   thing docs/MODULE-SCOPE.md 3.1 asks of this screen: at the property "the
   four questions over the property", at one section "the same four questions,
   narrowed. The quick-action cards change with what the section can do". They
   were not the same four. A section with a CMS answered published posts and
   published pages, a crawl-only one answered pages crawled and indexable, so a
   customer moving the switcher watched the questions themselves change and had
   no way to compare the two positions. A module that shows a different thing
   at each scope has not been thought about (3.1's opening line).

   All four are now asked of the PROPERTY PAGE UNIVERSE, the one set the
   property owns, read through GET /crawl/summary at whichever position the
   switcher is in:

     Pages          every URL this website serves
     Indexable      the ones nothing on them refuses to index
     Meta coverage  the share carrying both a title and a description
     Content plans  the plans aimed at it

   THE CRAWL IS THE PRIMARY SOURCE AND THE CMS IS THE FALLBACK, that way round
   on purpose: MODULE-SCOPE 5 says that for meta coverage the crawl is the
   BETTER measurement, being what the site actually serves rather than what a
   CMS claims. The fallback is per position and only when the crawl has read
   nothing, which is today's behaviour kept for the case it was right for.

   WHICHEVER SOURCE ANSWERED IS NAMED ON THE TILE. A number whose source is
   invisible is a number nobody can check, and these two sources disagree on
   purpose: Pages is the CMS lens and Page Inventory is the served lens (4.5).
   A tile that silently swapped between them would report the disagreement as a
   change in the website.

   Wired to:
     GET /crawl/summary?site_id=<anchor>[&section_id=<n>]   the four questions
     GET /wp/bulk-meta?siteUrl=...&type=posts|pages         the fallback, at one section
     GET /wp/bulk-meta-property?site_id=<anchor>&type=...   the fallback, at All
     GET /content-plans?site_id=<each section>              summed, see loadPlans
     GET /wp/audit-latest?siteUrl=...                       the audit badge, at one section
     GET /wp/audit-property?site_id=<anchor>                the audit badge, at All */
import { api } from '../lib/api.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const fmt = (n) => Number(n || 0).toLocaleString();

  // ---- the property's pieces, named the way every screen names them -------
  // A section's id IS its connection's site_id (3.12b: sections are not
  // replacing sites), so one accessor answers for both and nothing has to know
  // which of the two spellings a given list was built with.
  const idOf = (s) => (s && (s.site_id != null ? s.site_id : s.id));
  const nameOf = (s) => (s && s.name) || "this site";
  const fullUrlOf = (s) => {
    const raw = (s && (s.urlFull || s.url)) || "";
    return raw.startsWith("http") ? raw : (raw ? "https://" + raw : "");
  };
  // "A", "A and B", "A, B and C": a list of sections read as a sentence,
  // because naming them is what makes a summed figure checkable.
  function listNames(arr) {
    const n = (arr || []).map(x => (typeof x === "string" ? x : nameOf(x)));
    if (n.length <= 1) return n[0] || "";
    return n.slice(0, -1).join(", ") + " and " + n[n.length - 1];
  }

  // ---- shared 4-up stat (with optional delta) ----
  // `source` is {text, why}: the short line naming which read answered, with
  // the full sentence on hover. It is optional, so the other screens that use
  // SeoStat are untouched by it.
  function SeoStat({ icon, label, value, loading, delta, source }) {
    return (
      <div className="seo-stat">
        <div className="seo-stat__top"><span className="seo-stat__icon"><I name={icon} size={16} /></span><span className="seo-stat__label">{label}</span></div>
        <div className={"seo-stat__value" + (loading ? " seo-stat__loading" : "")}>{loading ? "…" : value}</div>
        {delta && <div className="seo-stat__foot"><DeltaTag {...delta} /></div>}
        {source && !loading && (
          <div className="seo-stat__foot seo-stat__foot--na">
            <span className="seo-stat__na" title={source.why || undefined}>{source.text}</span>
          </div>
        )}
      </div>
    );
  }
  // A stat this POSITION cannot answer, said as "not applicable" rather than
  // left reading zero.
  //
  // A crawl-only site has no CMS, so MarketLens holds none of its posts, pages
  // or stored meta. Rendering 0 and 0% there reported a perfectly healthy site
  // as an empty, unoptimised one, which is the exact false alarm this site type
  // exists to remove: the numbers were all technically correct and the picture
  // they painted was entirely wrong. The same now holds one level up: a website
  // nobody has crawled has no served page count, and 0 pages would be a claim
  // about the website rather than about the measurement. `action` is for the
  // one tile that can point at something that does measure the thing properly.
  function SeoStatNa({ icon, label, note, action }) {
    return (
      <div className="seo-stat">
        <div className="seo-stat__top"><span className="seo-stat__icon"><I name={icon} size={16} /></span><span className="seo-stat__label">{label}</span></div>
        <div className="seo-stat__value seo-stat__value--na">Not applicable</div>
        <div className="seo-stat__foot seo-stat__foot--na">
          <span className="seo-stat__na">{note}</span>
          {action}
        </div>
      </div>
    );
  }
  function DeltaTag({ dir, value, note }) {
    const cls = dir === "up" ? "seo-delta--up" : dir === "down" ? "seo-delta--down" : "seo-delta--flat";
    return <><span className={"seo-delta " + cls}>{dir === "flat" ? "±0" : <><I name={dir === "up" ? "chevron-up" : "chevron-down"} size={12} />{value}</>}</span>{note && <span className="seo-delta__note">{note}</span>}</>;
  }

  // "today" / "1d ago" / "5d ago": short freshness stamp for the audit badge.
  function ago(ts) {
    if (!ts) return "";
    const d = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
    return d <= 0 ? "today" : d === 1 ? "1d ago" : d + "d ago";
  }

  // Audit-health badge on the Site Audit quick card.
  //
  // It takes the NORMALISED shape built by loadAudit below rather than a stored
  // row, because the two positions read two different endpoints and the badge
  // must not care: at one section the latest stored snapshot for that site, at
  // All the property roll-up over every section's snapshot.
  //
  // THREE SECTIONS AUDITED ON THREE DAYS IS THREE AGES, and the oldest is the
  // one shown. An average of the three describes none of them and hides the
  // stale one, which is the only one worth acting on; the newest rides along
  // only when it differs, so a badge never implies a single freshness that the
  // website does not have.
  function AuditBadge({ a }) {
    if (!a) return <span className="seo-quick__audit seo-quick__audit--none"><I name="shield-check" size={12} />No audit yet</span>;
    // An unreadable snapshot and nobody having looked are not the same fact.
    if (a.failed) {
      return (
        <span className="seo-quick__audit is-warn"><I name="alert-triangle" size={12} />
          The stored audit could not be read, which is not the same as there not being one
        </span>
      );
    }
    if (a.none) {
      return (
        <span className="seo-quick__audit seo-quick__audit--none"><I name="shield-check" size={12} />
          {a.kind === "property" ? "No section of this website has been audited yet" : "No audit yet"}
        </span>
      );
    }
    const err = a.errors || 0, warn = a.warnings || 0;
    const clean = err + warn === 0;
    const age = (a.oldest && a.newest && a.oldest !== a.newest)
      ? `oldest ${ago(a.oldest)}, newest ${ago(a.newest)}`
      : ago(a.oldest || a.newest);
    return (
      <span className={"seo-quick__audit" + (err ? " is-error" : warn ? " is-warn" : " is-ok")}
            title={a.why || undefined}>
        <I name={clean ? "shield-check" : "alert-triangle"} size={12} />
        {clean ? "No issues" : `${err ? err + " errors" : ""}${err && warn ? " · " : ""}${warn ? warn + " warnings" : ""}`}
        {age && <span className="seo-quick__audit-when"> · {age}</span>}
      </span>
    );
  }

  // The sentence beside the badge, which exists because an empty findings list
  // and nobody having looked are the same thing on screen and not the same
  // fact. A section nobody has audited is NAMED rather than counted as clean.
  function auditNote(a) {
    if (!a || a.kind !== "property") return null;
    const bits = [];
    if (a.never && a.never.length) bits.push(`Never audited: ${listNames(a.never.map(x => x.name || `site ${x.section_id}`))}.`);
    if (a.unreadable && a.unreadable.length) bits.push(`Could not be read: ${listNames(a.unreadable.map(x => x.name || `site ${x.section_id}`))}.`);
    if (a.total > 0 && a.audited < a.total) bits.push(`These counts cover ${a.audited} of ${a.total} sections.`);
    if (!bits.length && a.total > 1) bits.push(`Every one of the ${a.total} sections has a stored audit, taken at different times.`);
    return bits.length ? bits.join(" ") : null;
  }

  // Every tool this hub can open, described once. The SET differs by position
  // and the descriptions do not, because a tool does not become a different
  // tool for being reached from All.
  const TOOLS = {
    "page-inventory": ["Page Inventory", "list", "Every page this website serves, with the title, description and status it served."],
    "performance": ["Performance", "trending-up", "Clicks, impressions, CTR and position from Google Search Console."],
    "site-audit": ["Site Audit", "shield-check", "Flag on-page SEO issues, by crawling the live HTML or by reading a CMS. Free, no AI cost."],
    "bulk-meta": ["Bulk Meta Editor", "edit", "Edit SEO titles, meta descriptions and focus keywords at scale."],
    "internal-linking": ["Internal Linking", "link", "Find orphan pages; opt-in AI suggestions for internal links."],
  };

  function SeoDashboard({ go, site, sectionId, sections }) {
    // The property's pieces. `sections` is this property's sites and the anchor
    // is always one of them; a boot with no list yet falls back to the anchor
    // alone so the screen renders rather than waiting.
    const list = (Array.isArray(sections) && sections.length) ? sections : (site ? [site] : []);
    const atAll = sectionId == null;
    // The section the filter names, resolved from the list rather than assumed
    // to be the anchor. They do move together today (picking a section sets
    // both), but a screen that reads the anchor when it means the section is
    // one refactor away from being quietly wrong.
    const section = atAll ? null : (list.find(s => idOf(s) === sectionId) || site);
    // A crawl-only section: a public part of the website with no CMS behind it,
    // which MarketLens reads by crawling and can never write to.
    const isCrawl = !!(section && section.source === "crawl");
    // THE ANCHOR, which is what authorises every request: enforceSiteScope
    // validates site_id against the session's sites and the property is derived
    // from it on the server rather than passed in. The section is an extra
    // filter on top, never a replacement for it.
    const siteId = idOf(site);

    // THE QUICK ACTION CARDS, which are the half of this screen that does
    // change with the position.
    //
    // At one section, today's rule is kept exactly: a crawl-only section gets
    // Page Inventory, Performance and Site Audit, because Bulk Meta and
    // Internal Linking both read and write a CMS and would be invitations to a
    // notice there. A section with a CMS gets Performance, Site Audit, Bulk
    // Meta and Internal Linking.
    //
    // At All, every tool including Page Inventory. Page Inventory is now the
    // property's served lens and is offered to every site (the nav gate
    // retired, MODULE-SCOPE 4.5), and nothing disappears at All in any case:
    // emptiness is a state a module renders, never a reason to remove the
    // module (1.1).
    const toolIds = atAll
      ? ["page-inventory", "performance", "site-audit", "bulk-meta", "internal-linking"]
      : (isCrawl
        ? ["page-inventory", "performance", "site-audit"]
        : ["performance", "site-audit", "bulk-meta", "internal-linking"]);

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reauditing, setReauditing] = useState(null);   // null | { done, total, name }

    // THE SECTION LIST ARRIVES AS A FRESH ARRAY ON EVERY RENDER, because the
    // shell derives it by filtering the account's sites. So the reads below
    // must not depend on the array's identity: a load that re-ran whenever its
    // props were rebuilt would call the API in a loop for as long as the screen
    // was open. They depend on a KEY built from what actually matters (which
    // sections, with which source and address), and read the objects through a
    // ref that is kept current.
    const listRef = React.useRef(list); listRef.current = list;
    const siteRef = React.useRef(site); siteRef.current = site;
    const sectionsKey = list.map(x => `${idOf(x)}:${x.source || ""}:${fullUrlOf(x)}`).join("|");
    const sectionOf = () => (atAll ? null : (listRef.current.find(x => idOf(x) === sectionId) || siteRef.current));

    // The fallback's CMS reads, per position. At one section this is the
    // existing per connection pair, unchanged. At All it is the property pair,
    // whose rows carry a `status` so published ones can be counted and whose
    // `sections` says which sections contributed: there is no single CMS behind
    // a website, so the list is a union and it has to name its parts.
    const loadCms = useCallback(async () => {
      const empty = { posts: [], pages: [], from: [], readable: false, capped: false, error: null };
      const sec = sectionOf();
      if (!atAll) {
        if (!sec) return empty;
        if (sec.source === "crawl") {
          return { ...empty, error: `${nameOf(sec)} has no CMS behind it, so there is nothing for a CMS count to read.` };
        }
        const u = fullUrlOf(sec);
        if (!u) return { ...empty, error: `${nameOf(sec)} has no address stored, so its CMS could not be read.` };
        const e = encodeURIComponent(u);
        let err = null;
        const [p, pg] = await Promise.all([
          api(`/wp/bulk-meta?siteUrl=${e}&type=posts&perPage=100`).catch((x) => { err = (x && x.message) || "the CMS could not be read"; return { items: [] }; }),
          api(`/wp/bulk-meta?siteUrl=${e}&type=pages&perPage=100`).catch((x) => { err = err || (x && x.message) || "the CMS could not be read"; return { items: [] }; }),
        ]);
        const posts = (p && p.items) || [], pages = (pg && pg.items) || [];
        return {
          posts, pages, from: [nameOf(sec)], readable: !err, error: err,
          // The list is read 100 rows at a time, so a site at the cap has MORE
          // than this many. A count shown to a human must not pretend a capped
          // list is an inventory, so the tile marks it as a floor.
          capped: posts.length >= 100 || pages.length >= 100,
        };
      }
      if (siteId == null) return empty;
      const [p, pg] = await Promise.all([
        api(`/wp/bulk-meta-property?site_id=${siteId}&type=posts&perPage=100`).catch(() => null),
        api(`/wp/bulk-meta-property?site_id=${siteId}&type=pages&perPage=100`).catch(() => null),
      ]);
      if (!p && !pg) return { ...empty, error: "the website's CMS rows could not be read" };
      const secs = [...(((p && p.sections) || [])), ...(((pg && pg.sections) || []))];
      const cms = secs.filter(x => x.section_source !== "crawl");
      const names = [...new Set(cms.filter(x => !x.error).map(x => x.section_name || `site ${x.section_id}`))];
      const failed = [...new Set(cms.filter(x => x.error).map(x => x.section_name || `site ${x.section_id}`))];
      return {
        // Only the rows a CMS holds. A crawl-only section contributes crawled
        // URLs to that answer and those belong to the served lens, which is the
        // primary source this fallback exists because nothing answered.
        posts: ((p && p.items) || []).filter(r => r.read_from !== "crawl"),
        pages: ((pg && pg.items) || []).filter(r => r.read_from !== "crawl"),
        from: names,
        readable: names.length > 0,
        capped: secs.some(x => x.capped),
        error: failed.length ? `${listNames(failed)} could not be read, so none of its rows are counted here.` : null,
      };
    }, [atAll, sectionId, siteId, sectionsKey]);

    // CONTENT PLANS IS A SUM OF PER SECTION READS, and it is the only tile that
    // is one, which is why the reason is written here rather than assumed.
    //
    // The Content Planner is SECTION scoped and is not changing (MODULE-SCOPE
    // 3: a plan that cannot say where the piece will be published is not a
    // plan), so content_plans is read through its own per connection filter and
    // there is no property read to call. Summing is therefore not a shortcut
    // around a missing endpoint: it is the arithmetic that matches where the
    // data lives. The sections that contributed are named for the same reason
    // the other tiles name their source, and because three sections' plans are
    // three lists rather than one.
    const loadPlans = useCallback(async () => {
      const sec = sectionOf();
      const targets = atAll ? listRef.current : (sec ? [sec] : []);
      const per = await Promise.all(targets.map(async (s) => {
        const id = idOf(s);
        if (id == null) return { name: nameOf(s), count: 0, error: "it has no connection id" };
        try {
          const r = await api(`/content-plans?site_id=${id}`);
          // Live state: /content-plans answers the whole list, uncapped, so the
          // length of what it returned IS the count.
          return { name: nameOf(s), count: Array.isArray(r) ? r.length : ((r && r.items && r.items.length) || 0), error: null };
        } catch (e) {
          return { name: nameOf(s), count: 0, error: (e && e.message) || "it could not be read" };
        }
      }));
      return {
        per,
        total: per.reduce((n, x) => n + x.count, 0),
        from: per.filter(x => !x.error).map(x => x.name),
        failed: per.filter(x => x.error).map(x => x.name),
      };
    }, [atAll, sectionId, sectionsKey]);

    // The audit badge's two positions, normalised to one shape so the badge
    // itself never branches on which endpoint answered.
    const loadAudit = useCallback(async () => {
      // A FAILED READ IS NOT AN UNAUDITED WEBSITE, and the badge says which.
      // Reporting "no audit yet" because the request fell over would send
      // somebody to run an audit that is already stored.
      if (!atAll) {
        const u = fullUrlOf(sectionOf());
        if (!u) return null;
        let r = null;
        try { r = await api(`/wp/audit-latest?siteUrl=${encodeURIComponent(u)}`); }
        catch { return { kind: "section", failed: true }; }
        const a = r && r.audit;
        if (!a) return { kind: "section", none: true, never: [], unreadable: [], audited: 0, total: 1 };
        return {
          kind: "section", errors: Number(a.errors || 0), warnings: Number(a.warnings || 0),
          oldest: a.updated_at, newest: a.updated_at, never: [], unreadable: [], audited: 1, total: 1,
        };
      }
      if (siteId == null) return null;
      let r = null;
      try { r = await api(`/wp/audit-property?site_id=${siteId}`); }
      catch { return { kind: "property", failed: true }; }
      const sum = r.summary || {};
      const secs = Array.isArray(r.sections) ? r.sections : [];
      return {
        kind: "property",
        errors: Number(sum.error || 0), warnings: Number(sum.warning || 0),
        oldest: r.oldest_audit || null, newest: r.newest_audit || null,
        never: r.sections_never_audited || [], unreadable: r.sections_unreadable || [],
        audited: secs.filter(x => x.audited === true).length, total: secs.length,
        none: !r.oldest_audit, why: r.coverage_why || null,
      };
    }, [atAll, sectionId, siteId, sectionsKey]);

    const load = useCallback(async () => {
      setLoading(true);
      try {
        const qs = atAll ? "" : `&section_id=${sectionId}`;
        // The crawl is read FIRST and on its own, because what it answers
        // decides whether the CMS has to be read at all. The extra round trip
        // only happens in the fallback case, which is the case where there is
        // nothing left to be fast about.
        let crawl = null, crawlErr = null;
        if (siteId != null) {
          try { crawl = await api(`/crawl/summary?site_id=${siteId}${qs}`); }
          catch (e) { crawlErr = (e && e.message) || "the page inventory could not be read"; }
        }
        // The crawl ANSWERED when it has rows, or when it read the website and
        // found none in this slice: a section with no pages is READ AND EMPTY,
        // which is an answer about the website and not a gap in the
        // measurement. The CMS is only consulted when nothing has been read.
        const answered = !!(crawl && (Number(crawl.pages || 0) > 0 || (crawl.state && crawl.state.crawled)));
        const [cms, plans, audit] = await Promise.all([
          answered ? null : loadCms(),
          loadPlans(),
          loadAudit(),
        ]);
        setStats({ crawl, crawlErr, answered, cms, plans, audit, failedAudits: [] });
      } catch {
        setStats({ crawl: null, crawlErr: "the dashboard could not be loaded", answered: false, cms: null, plans: null, audit: null });
      } finally { setLoading(false); }
    }, [atAll, sectionId, siteId, loadCms, loadPlans, loadAudit]);
    useEffect(() => { load(); }, [load]);

    // RE-AUDIT RUNS A CRAWL OF ONE SITE, so at All it has no single target and
    // is run once per section, in turn, naming the one in flight. Running it
    // against the anchor alone would silently audit a third of the website and
    // then refresh a badge that describes the whole of it, which is the exact
    // mistake MODULE-SCOPE 4.1 records: a narrower crawl replacing a wider one
    // "and for a while the badge was describing half a site while looking
    // complete".
    const auditTargets = (atAll ? list : (section ? [section] : [])).filter(s => fullUrlOf(s));
    const runReaudit = useCallback(async () => {
      if (!auditTargets.length) return;
      const failed = [];
      for (let i = 0; i < auditTargets.length; i++) {
        const t = auditTargets[i];
        setReauditing({ done: i, total: auditTargets.length, name: nameOf(t) });
        try { await api(`/wp/audit-public?siteUrl=${encodeURIComponent(fullUrlOf(t))}&maxUrls=100`); }
        catch { failed.push(nameOf(t)); }
      }
      setReauditing(null);
      const a = await loadAudit().catch(() => null);
      setStats(prev => ({ ...(prev || {}), audit: a, failedAudits: failed }));
    }, [auditTargets, loadAudit]);

    const s = stats || {};
    const c = s.crawl || null;
    const answered = !!s.answered;
    const cms = s.cms || null;
    const plans = s.plans || null;

    // ---- the four tiles' sources, named -----------------------------------
    // READ AND EMPTY IS AN ANSWER, so the zero it produces is labelled as one
    // rather than left bare. The crawling has read the website and none of its
    // pages is in this slice: a fact about the website, not a gap in the
    // measurement, and the server's own sentence says so.
    const readEmpty = !!(c && c.state && c.state.read_and_empty);
    const crawlSource = readEmpty ? {
      text: atAll ? "Crawled, and the website serves none" : `${nameOf(section)} was crawled and is empty`,
      why: c.state.why,
    } : {
      text: (atAll ? "Measured by crawling this website" : `Measured by crawling, narrowed to ${nameOf(section)}`)
        + (c && c.crawled_at ? ` · last read ${ago(c.crawled_at)}` : ""),
      why: "Read from the page inventory: the HTML this website actually serves, rather than what a CMS claims it "
        + "holds. The two disagree on purpose, and for meta coverage this is the better measurement of the two. "
        + "A website read by several crawls was read at several times, and the stamp above is the most recent of "
        + "them: Page Inventory reports each crawl's own run.",
    };
    const cmsSource = cms && {
      text: `From the CMS of ${listNames(cms.from) || "this website"}`
        + (cms.capped ? " · 100 rows per list, so this is a floor" : ""),
      why: "Nothing has been crawled at this position, so these figures come from what the CMS holds rather than "
        + "from what the website serves. A CMS count includes what is published and what the crawl would also have "
        + "found, and the two numbers are allowed to differ."
        + (cms.capped ? " Each CMS list is read 100 rows at a time, so a section at the cap holds more than this." : "")
        + (cms.error ? ` ${cms.error}` : ""),
    };
    // The sentence for every empty case comes from the server: `state.why`
    // tells "nothing has crawled this website yet" apart from "this section was
    // read and is genuinely empty", and they are different answers that a bare
    // 0 would merge.
    const emptyWhy = (c && c.state && c.state.why) || s.crawlErr
      || "Nothing has read what this website serves yet. Open Page Inventory and press Refresh.";
    const toInventory = (
      <button className="bi-btn bi-btn--sm" onClick={() => go && go("page-inventory")}><I name="list" size={14} />Page Inventory</button>
    );

    const publishedOf = (arr) => (arr || []).filter(x => x.status === "publish").length;
    const cmsPages = cms ? publishedOf(cms.posts) + publishedOf(cms.pages) : 0;
    // The Pages tile names the two halves of the CMS answer rather than just
    // the source, because "pages" out of a CMS means published posts plus
    // published pages and a reader comparing it with the served count needs to
    // know that is what they are comparing.
    const cmsPagesSource = cms && cmsSource && {
      text: `${fmt(publishedOf(cms.posts))} published posts and ${fmt(publishedOf(cms.pages))} published pages, ${cmsSource.text.replace(/^From the/, "from the")}`,
      why: cmsSource.why,
    };
    const cmsAll = cms ? cms.posts.concat(cms.pages) : [];
    const cmsWithMeta = cmsAll.filter(x => String(x.seo_title || "").trim() && String(x.meta_description || "").trim()).length;
    const cmsCoverage = (cms && cms.readable && cmsAll.length) ? Math.round(cmsWithMeta / cmsAll.length * 100) : null;

    const sectionCount = list.length;
    // Null is an answer: a connection with no property behind it has no
    // website to aggregate, and saying so beats calling one connection "the
    // whole website".
    const notGrouped = !!(c && c.scope && c.scope.kind === "connection");
    const scopeText = atAll
      ? (notGrouped
        ? `${nameOf(site)} is not grouped under a property yet, so the whole website here is this one connection.`
        : `The whole website: ${sectionCount} section${sectionCount === 1 ? "" : "s"}${sectionCount > 1 ? ` (${listNames(list)})` : ""}. The four questions below are counted over all of it.`)
      : `Narrowed to ${nameOf(section)}. The same four questions, counted over this section only.`;

    const note = auditNote(s.audit);

    return (
      <div className="bi-page">
        <div className="bi-header">
          <div className="bi-header__titles">
            <h1 className="bi-title"><span className="bi-title__icon"><I name="gauge" size={20} /></span>SEO Dashboard</h1>
            <div className="bi-subtitle">
              {atAll ? "The whole website" : "One section"} · {atAll ? ((site && (site.property_name || site.url)) || "this website") : nameOf(section)}
            </div>
          </div>
          <div className="bi-header-actions"><button className="bi-btn" onClick={load}><I name="refresh" size={16} />Refresh</button></div>
        </div>

        <div className="seo-scope"><span className="seo-scope__label">{scopeText}</span></div>

        <div className="seo-stat-row">
          {/* PAGES. The served universe when anything has been crawled, the
              CMS count when nothing has, and "not applicable" when neither can
              answer: 0 pages would be a claim about the website where the truth
              is that nothing has measured it. */}
          {answered
            ? <SeoStat icon="file" label="Pages" value={fmt(c.pages)} loading={loading} source={crawlSource} />
            : (cms && cms.readable)
              ? <SeoStat icon="file" label="Pages" value={fmt(cmsPages) + (cms.capped ? "+" : "")} loading={loading} source={cmsPagesSource} />
              : <SeoStatNa icon="file" label="Pages" note={emptyWhy} action={toInventory} />}

          {/* INDEXABLE. Only the served lens can answer this at all: a CMS
              records a post's status, not whether the page it renders refuses
              indexing, so in the fallback this is a genuine "not applicable"
              rather than a missing number. */}
          {answered
            ? <SeoStat icon="search" label="Indexable" value={fmt(c.indexable)} loading={loading} source={crawlSource} />
            : <SeoStatNa icon="search" label="Indexable" note={"A CMS does not record whether a served page refuses indexing, so this is read from the HTML the site serves. " + emptyWhy} action={toInventory} />}

          {/* META COVERAGE. The crawl measures what a visitor and a search
              engine really get, which MODULE-SCOPE 5 calls the better of the
              two measurements, so the CMS answer is the fallback here as well.
              `coverage` is null rather than 0 when there is nothing to divide
              by, and a null renders as the unknown it is. */}
          {answered
            ? (c.coverage == null
              ? <SeoStatNa icon="gauge" label="Meta coverage" note={emptyWhy} action={toInventory} />
              : <SeoStat icon="gauge" label="Meta coverage" value={c.coverage + "%"} loading={loading} source={crawlSource} />)
            : (cmsCoverage == null
              ? <SeoStatNa icon="gauge" label="Meta coverage" note={emptyWhy} action={toInventory} />
              : <SeoStat icon="gauge" label="Meta coverage" value={cmsCoverage + "%"} loading={loading} source={cmsSource} />)}

          {/* CONTENT PLANS. The one tile with no property read behind it: see
              loadPlans for why it is a sum of per section reads and why the
              sections are named. */}
          {plans
            ? <SeoStat icon="calendar-days" label="Content plans" value={fmt(plans.total)} loading={loading}
                source={{
                  text: atAll
                    ? `Summed over ${plans.from.length} section${plans.from.length === 1 ? "" : "s"}${plans.from.length ? `: ${listNames(plans.from)}` : ""}`
                    : `${nameOf(section)}'s own plans`,
                  why: "The Content Planner is section scoped, because a plan that cannot say where the piece will be "
                    + "published is not a plan. So this figure is the sum of each section's own plans rather than one "
                    + "read over the website, and the sections it was summed over are named."
                    + (plans.failed.length ? ` Not counted: ${listNames(plans.failed)}.` : ""),
                }} />
            : <SeoStatNa icon="calendar-days" label="Content plans" note="No section of this website could be asked for its plans." />}
        </div>

        <div className="seo-quick-grid">
          {toolIds.map((id) => {
            const [name, icon, desc] = TOOLS[id];
            return (
              <div className="bi-card seo-quick-card" key={id} onClick={() => go && go(id)}>
                <div className="seo-quick">
                  <span className="seo-quick__icon"><I name={icon} size={20} /></span>
                  <div style={{ minWidth: 0 }}>
                    <div className="seo-quick__name">{name}</div>
                    <div className="seo-quick__desc">{desc}</div>
                    {id === "site-audit" && !loading && <AuditBadge a={s.audit} />}
                    {id === "site-audit" && !loading && note && <div className="seo-quick__desc">{note}</div>}
                    {id === "site-audit" && !loading && s.failedAudits && s.failedAudits.length > 0 && (
                      <div className="seo-quick__desc">Could not be audited just now: {listNames(s.failedAudits)}.</div>
                    )}
                  </div>
                </div>
                <div className="seo-quick__actions">
                  {id === "site-audit" && (
                    <button className="bi-btn bi-btn--sm" disabled={!!reauditing || !auditTargets.length}
                      title={auditTargets.length
                        ? (atAll && auditTargets.length > 1
                          ? `An audit crawls one site, so this runs once per section, in turn: ${listNames(auditTargets)}.`
                          : `Crawl ${nameOf(auditTargets[0])} again and refresh this badge.`)
                        : "No section of this website has an address to crawl."}
                      onClick={(e) => { e.stopPropagation(); runReaudit(); }}>
                      {reauditing
                        ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} />{`Auditing ${reauditing.name} (${reauditing.done + 1} of ${reauditing.total})…`}</>
                        : <><I name="shield-check" size={14} />{(atAll && auditTargets.length > 1) ? "Re-audit every section" : "Re-audit now"}</>}
                    </button>
                  )}
                  <button className="bi-btn bi-btn--sm seo-quick__btn" onClick={(e) => { e.stopPropagation(); go && go(id); }}>Open<I name="arrow-right" size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  window.MlSeoDashboard = { SeoDashboard, SeoStat, DeltaTag };
})();
